'use client';

import { isEmpty } from 'lodash';
import { toast } from 'react-toastify';
import React, { useCallback, useEffect } from 'react';

import useInfiniteScroll from 'src/hooks/useInfiniteScroll';
import { createAbortSignal } from 'src/helpers/utils';
import ActivityLogCard from
  'src/components/job-slide-out/components/activity-log/activity-log-card';

import { useAppDispatch, useAppSelector } from 'src/hooks/redux-hooks';
import { fetchJobActivityLog } from 'src/redux/jobs/api';
import { updateSlideOut } from 'src/redux/jobs/slice';

import { ActivityLogLoader } from 'src/assets/images/job-details-slide-out/job-details-slide-out';
import type { ActivityLogEntry } from 'src/types';

const activityLogSkeltonLoader = (): React.ReactElement => (
  <React.Fragment>
    <div className="common-loading w-15 mb-2" />
    <div className="common-loading-lg mb-2" />
    <div className="common-loading w-36 mb-2" />
    <div className="common-loading-lg mb-2" />
    <div className="common-loading w-15 mb-2" />
    <div className="common-loading-lg mb-2" />
    <div className="common-loading w-15 mb-2" />
    <div className="common-loading-xl mb-2" />
  </React.Fragment>
);

const ActivityLog: React.FC = () => {
  const dispatch = useAppDispatch();
  const type = useAppSelector((state) => state?.jobDetails?.slideOut?.type);
  const jobId = useAppSelector((
    state,
  ) => state?.jobDetails?.slideOut?.jobId);

  const activityLog = useAppSelector((state) => state?.jobDetails?.activityLog);
  const metaData = useAppSelector(
    (state) => (state?.jobDetails?.activityLog as Record<string, unknown>)?.meta,
  ) as Record<string, unknown> | undefined;

  const serviceRequestId = useAppSelector((
    state,
  ) => state?.jobDetails?.slideOut?.srId);
  const hasInitialActivityLogApiTriggered = useAppSelector((
    state,
  ) => state?.jobDetails?.slideOut?.hasInitialActivityLogApiTriggered);
  const isActivityLogLoading = useAppSelector(
    (state) => state?.jobDetails?.isActivityLogLoading,
  );
  const scrollToLumiIntake = useAppSelector(
    (state) => state?.jobDetails?.slideOut?.scrollToLumiIntake,
  );

  const activityLogData = (
    activityLog as Record<string, unknown>
  )?.data as ActivityLogEntry[] | undefined;

  useEffect(() => {
    if (!scrollToLumiIntake || !hasInitialActivityLogApiTriggered || isActivityLogLoading) return;

    const lumiEntry = activityLogData?.find(
      (item) => item.event === 'lumi.summary.added',
    );

    if (lumiEntry) {
      const el = document.getElementById(lumiEntry.activity_uuid);
      el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      dispatch(updateSlideOut({ scrollToLumiIntake: false }));
    } else if (metaData?.next_cursor) {
      dispatch(
        fetchJobActivityLog(serviceRequestId)({
          cursor: metaData.next_cursor,
          ...(type === 'jobDetails' ? { job_id: jobId } : {}),
        }),
      ).unwrap().catch((e: Record<string, unknown>) => {
        if (e?.actionType !== 'canceled') {
          toast((e?.message as string) || 'Something went wrong', { type: 'error' });
        }
        dispatch(updateSlideOut({ scrollToLumiIntake: false }));
      });
    } else {
      dispatch(updateSlideOut({ scrollToLumiIntake: false }));
    }
  }, [scrollToLumiIntake, hasInitialActivityLogApiTriggered, isActivityLogLoading, activityLogData, metaData?.next_cursor, dispatch, serviceRequestId, type, jobId]);

  const getActivityLogs = useCallback(() => {
    if (!isActivityLogLoading
        && hasInitialActivityLogApiTriggered
        && serviceRequestId
        && metaData?.next_cursor
    ) {
      dispatch(
        fetchJobActivityLog(serviceRequestId)({
          cursor: metaData?.next_cursor,
          ...(type === 'jobDetails' ? { job_id: jobId } : {}),
        }),
      ).unwrap().catch((e: Record<string, unknown>) => {
        toast((e?.message as string) || 'Something went wrong', { type: 'error' });
      });
    }
  }, [
    isActivityLogLoading,
    hasInitialActivityLogApiTriggered,
    serviceRequestId,
    metaData?.next_cursor,
    dispatch,
    type,
    jobId]);

  const targetRef = useInfiniteScroll(
    true,
    getActivityLogs,
    'activityLogScroll',
    '50px',
    1.0,
  );

  useEffect(() => {
    const controller = createAbortSignal();
    if (serviceRequestId
      && !hasInitialActivityLogApiTriggered) {
      dispatch(fetchJobActivityLog(serviceRequestId, controller?.signal)({
        ...(type === 'jobDetails' ? { job_id: jobId } : {}),
      }))?.unwrap().then(() => {
        setTimeout(() => {
          dispatch(updateSlideOut({ hasInitialActivityLogApiTriggered: true }));
        }, 100);
      }).catch((e: Record<string, unknown>) => {
        if (e?.actionType !== 'canceled') {
          toast((e?.message as string) || 'Something went wrong', { type: 'error' });
        }
      });
    }
    // Cleanup function to abort any pending requests when component unmounts or dependencies change
    return () => {
      controller?.abort();
    };
  }, [dispatch, hasInitialActivityLogApiTriggered, jobId, serviceRequestId, type]);
  return (
    <div className="job-content-activity-log">
      <div
        className={[
          'activity-log-outer',
          isEmpty(activityLogData) && !isActivityLogLoading
            ? 'no-data-outer' : '',
        ].join(' ')}
        id="activity-log-list-container"
      >
        {(isActivityLogLoading && !hasInitialActivityLogApiTriggered) ? (
          activityLogSkeltonLoader()
        ) : ''}
        {isEmpty(activityLogData) && !isActivityLogLoading ? (
          <div className="no-data mt-3">
            No activity log available for this job
          </div>
        ) : ''}
        {!isEmpty(activityLogData) && Array.isArray(activityLogData) ? (
          activityLogData?.map((item, index, mappingArray) => (
            <React.Fragment key={item?.activity_uuid} >
              <ActivityLogCard activityLog={item} />
              {!isActivityLogLoading
                && index === mappingArray.length - 1
                ? <div ref={targetRef} /> : ''}
            </React.Fragment>
          ))
        ) : ''}

        {(isActivityLogLoading && metaData?.next_cursor && hasInitialActivityLogApiTriggered) ? (
          <div className="activity-log loading">
            <div className="activity-log-inner">
              <div className="activity-loader">
                <ActivityLogLoader />
                <div className="loader-text">Loading...</div>
              </div>
            </div>

          </div>
        ) : ''}
        {' '}
      </div>
    </div>

  );
};

export default ActivityLog;
